import { useState, useMemo, useCallback } from 'react';
import { Loader2, Receipt, ChevronRight, AlertCircle } from 'lucide-react';
import { Header } from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useWeeklyVisits } from '@/features/visits/hooks/useWeeklyVisits';
import { usePaidOrders, useOrderWaybill, groupByClient } from './hooks/useWaybills';
import type { PaidOrderItem } from '@/api/legacy';
import type { OwnershipFilterValue } from '@/api/visits';

type FilterValue = OwnershipFilterValue[] | 'all';

const OWNERSHIP_FILTER_KEY = 'visits-ownership-filter';

function loadOwnershipFilter(): FilterValue {
  try {
    const saved = localStorage.getItem(OWNERSHIP_FILTER_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed === 'all' || (Array.isArray(parsed) && parsed.length > 0)) {
        return parsed;
      }
    }
  } catch {
    // Ignore parse errors
  }
  return ['own'];
}

interface WaybillItemProps {
  item: PaidOrderItem;
  onOrder: () => void;
  isPending: boolean;
}

function WaybillItem({ item, onOrder, isPending }: WaybillItemProps) {
  return (
    <div className="flex items-center justify-between p-3 bg-background rounded border">
      <span className="text-sm flex-1 mr-2">{item.name}</span>
      <Button
        variant="outline"
        size="sm"
        onClick={onOrder}
        disabled={isPending}
        className="shrink-0"
      >
        {isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Заказать'}
      </Button>
    </div>
  );
}

interface ClientGroupProps {
  clientName: string;
  items: PaidOrderItem[];
  onOrderItem: (id: number) => void;
  pendingId: number | null;
}

function ClientGroup({ clientName, items, onOrderItem, pendingId }: ClientGroupProps) {
  const [expanded, setExpanded] = useState(true);

  return (
    <div className="border rounded-lg overflow-hidden bg-card">
      <button
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <span className="font-medium text-sm">{clientName}</span>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="text-xs">
            {items.length}
          </Badge>
          <ChevronRight
            className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
          />
        </div>
      </button>
      {expanded && (
        <div className="p-2 space-y-2 border-t bg-muted/20">
          {items.map((item) => (
            <WaybillItem
              key={item.id}
              item={item}
              onOrder={() => onOrderItem(Number(item.id))}
              isPending={pendingId === Number(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export function WaybillsPage() {
  const [pendingId, setPendingId] = useState<number | null>(null);
  const ownershipFilters = loadOwnershipFilter();

  // Загружаем клиентов из того же источника что и VisitsPage
  const { data: weeklyData, isLoading: isLoadingVisits } = useWeeklyVisits(ownershipFilters);

  // Извлекаем имена клиентов
  const clientNames = useMemo(() => {
    if (!weeklyData?.visits) return [];
    const names = new Set<string>();
    for (const visit of weeklyData.visits) {
      if (visit.client?.name) {
        names.add(visit.client.name);
      }
    }
    return Array.from(names);
  }, [weeklyData?.visits]);

  // Загружаем накладные с includeUserOrders=true для страницы
  const { data: orders, isLoading: isLoadingOrders, error } = usePaidOrders(
    clientNames,
    true // includeUserOrders - показываем также заказы созданные пользователем
  );

  const orderWaybill = useOrderWaybill();

  const groupedOrders = useMemo(() => {
    if (!orders) return new Map<string, PaidOrderItem[]>();
    return groupByClient(orders);
  }, [orders]);

  const handleOrder = useCallback(
    (id: number) => {
      setPendingId(id);
      orderWaybill.mutate(id, {
        onSettled: () => setPendingId(null),
      });
    },
    [orderWaybill]
  );

  const isLoading = isLoadingVisits || isLoadingOrders;
  const totalCount = orders?.length || 0;

  return (
    <div className="min-h-screen bg-background">
      <Header
        title="Заказ накладных"
        subtitle={totalCount > 0 ? `${totalCount} товар(ов)` : undefined}
      />

      <main className="p-4 space-y-3">
        {isLoading && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center py-12 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mb-4" />
            <p className="text-muted-foreground">Ошибка загрузки</p>
          </div>
        )}

        {!isLoading && !error && groupedOrders.size === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <Receipt className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="font-medium">Нет товаров для выписки</p>
            <p className="text-sm text-muted-foreground mt-1">
              Все накладные уже заказаны
            </p>
          </div>
        )}

        {!isLoading && !error && groupedOrders.size > 0 && (
          <div className="space-y-3">
            {Array.from(groupedOrders.entries()).map(([clientName, items]) => (
              <ClientGroup
                key={clientName}
                clientName={clientName}
                items={items}
                onOrderItem={handleOrder}
                pendingId={pendingId}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
